import * as actions from './actions';

import { ActionType, createReducer } from 'typesafe-actions';

import { GetTransactionsPageSuccess, TransactionDetails, TransactionsState } from './types';
import { ACTION_STATUSES } from 'shared/consts';

const initialState: TransactionsState = {
  transactions: null,
  pastTransactions: null,
  transactionsTotal: null,
  pastTransactionsTotal: null,
  transactionsNextOffset: 0,
  pastTransactionsNextOffset: 0,
  transaction: null,
  getTransaction: {
    status: null,
    error: null,
  },
  getTransactions: {
    status: null,
    error: null,
  },
  getPastTransactions: {
    status: null,
    error: null,
  },
  pastLoading: false,
  patchTransaction: {
    status: null,
    error: null,
    type: null,
  },
  loading: false,
  processed: false,
  refreshing: false,
  error: null,
  getRedirectTransaction: {
    status: null,
    error: null,
  },
  redirectTransaction: null,
  checkIn: {
    status: null,
    error: null,
  },
  currentTransaction: null,
};

const getTransactionRequestHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  getTransaction: {
    error: null,
    status: ACTION_STATUSES.PENDING,
  },
});

const getTransactionSuccessHandler = (
  state: TransactionsState,
  { payload }: ActionType<typeof actions.getTransaction.success>,
): TransactionsState => ({
  ...state,
  transaction: payload,
  getTransaction: {
    error: null,
    status: ACTION_STATUSES.FULFILLED,
  },
});

const getTransactionFailureHandler = (
  state: TransactionsState,
  { payload: error }: ActionType<typeof actions.getTransaction.failure>,
): TransactionsState => ({
  ...state,
  getTransaction: {
    error: error.error,
    status: ACTION_STATUSES.REJECTED,
  },
});

const resetTransactionHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  transaction: null,
  getTransaction: {
    error: null,
    status: null,
  },
});

/* Offset decides, not length: `offset === 0` is a fresh read and replaces the list, so a booking that
 * has left the tab disappears; anything further in extends it.
 *
 * Extending dedupes by uuid and keeps the row already held, which may carry a `checkIn.success` the
 * re-read predates. The mirror case, a row the sliding window skips past, needs a cursor. */
const appendPage = (
  current: TransactionDetails[] | null,
  payload: GetTransactionsPageSuccess,
): TransactionDetails[] => {
  if (payload.offset <= 0) {
    return payload.transactions;
  }

  const held = current ?? [];
  const heldUuids = new Set(held.map(({ uuid }) => uuid));

  return [...held, ...payload.transactions.filter(({ uuid }) => !heldUuids.has(uuid))];
};

/* An empty window is the end whatever `total` says: a booking entering ahead pushes `total` out of reach. */
const nextOffset = (payload: GetTransactionsPageSuccess): number =>
  payload.transactions.length ? payload.offset + payload.transactions.length : payload.total;

const getTransactionsRequestHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  loading: true,
  processed: false,
  refreshing: false,
  getTransactions: {
    error: null,
    status: ACTION_STATUSES.PENDING,
  },
});

const getTransactionsSuccessHandler = (
  state: TransactionsState,
  { payload }: ActionType<typeof actions.getTransactions.success>,
): TransactionsState => ({
  ...state,
  transactions: appendPage(state.transactions, payload),
  transactionsTotal: payload.total,
  transactionsNextOffset: nextOffset(payload),
  getTransactions: {
    error: null,
    status: ACTION_STATUSES.FULFILLED,
  },
  loading: false,
  processed: false,
  refreshing: false,
  error: null,
});

const getTransactionsFailureHandler = (
  state: TransactionsState,
  { payload: error }: ActionType<typeof actions.getTransactions.failure>,
): TransactionsState => ({
  ...state,
  getTransactions: {
    error: error.error,
    status: ACTION_STATUSES.REJECTED,
  },
  loading: false,
  processed: false,
  refreshing: false,
  error: error.error,
});

/* Each of the three touches only the `past*` fields rather than sharing a handler parameterised by
 * slice: a past response must never write `transactions`. A failure leaves the rows on screen. */
const getPastTransactionsRequestHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  pastLoading: true,
  getPastTransactions: {
    error: null,
    status: ACTION_STATUSES.PENDING,
  },
});

const getPastTransactionsSuccessHandler = (
  state: TransactionsState,
  { payload }: ActionType<typeof actions.getPastTransactions.success>,
): TransactionsState => ({
  ...state,
  pastTransactions: appendPage(state.pastTransactions, payload),
  pastTransactionsTotal: payload.total,
  pastTransactionsNextOffset: nextOffset(payload),
  getPastTransactions: {
    error: null,
    status: ACTION_STATUSES.FULFILLED,
  },
  pastLoading: false,
});

const getPastTransactionsFailureHandler = (
  state: TransactionsState,
  { payload: error }: ActionType<typeof actions.getPastTransactions.failure>,
): TransactionsState => ({
  ...state,
  getPastTransactions: {
    error: error.error,
    status: ACTION_STATUSES.REJECTED,
  },
  pastLoading: false,
});

const patchTransactionRequestHandler = (
  state: TransactionsState,
  { payload }: ActionType<typeof actions.patchTransaction.request>,
): TransactionsState => ({
  ...state,
  patchTransaction: {
    error: null,
    status: ACTION_STATUSES.PENDING,
    type: payload.status ?? payload.status_3ds,
  },
});

const patchTransactionSuccessHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  patchTransaction: {
    ...state.patchTransaction,
    error: null,
    status: ACTION_STATUSES.FULFILLED,
  },
});

const resetPatchTransaction = (state: TransactionsState): TransactionsState => ({
  ...state,
  patchTransaction: {
    type: null,
    error: null,
    status: null,
  },
});

const patchTransactionFailureHandler = (
  state: TransactionsState,
  { payload: error }: ActionType<typeof actions.patchTransaction.failure>,
): TransactionsState => ({
  ...state,
  patchTransaction: {
    ...state.patchTransaction,
    error: error.error,
    status: ACTION_STATUSES.REJECTED,
  },
});

const getRedirectTransactionRequestHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  getRedirectTransaction: {
    error: null,
    status: ACTION_STATUSES.PENDING,
  },
});

const getRedirectTransactionSuccessHandler = (
  state: TransactionsState,
  { payload }: ActionType<typeof actions.getRedirectTransaction.success>,
): TransactionsState => ({
  ...state,
  redirectTransaction: payload,
  getRedirectTransaction: {
    error: null,
    status: ACTION_STATUSES.FULFILLED,
  },
});

const getRedirectTransactionFailureHandler = (
  state: TransactionsState,
  { payload: error }: ActionType<typeof actions.getRedirectTransaction.failure>,
): TransactionsState => ({
  ...state,
  getRedirectTransaction: {
    error: error.error,
    status: ACTION_STATUSES.REJECTED,
  },
});

const resetRedirectTransactionHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  redirectTransaction: null,
  getRedirectTransaction: {
    error: null,
    status: null,
  },
});

const checkInRequestHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  checkIn: {
    error: null,
    status: ACTION_STATUSES.PENDING,
  },
});

const checkInSuccessHandler = (
  state: TransactionsState,
  { payload }: ActionType<typeof actions.checkIn.success>,
): TransactionsState => {
  const { transactionId, ...checkInData } = payload;

  const updatedCurrentTransaction = state.currentTransaction
    ? {
        ...state.currentTransaction,
        details: {
          ...state.currentTransaction.details,
          check_in: { ...checkInData },
        },
      }
    : null;

  const updatedTransactions = state.transactions.map(transaction =>
    transaction.id === transactionId
      ? {
          ...transaction,
          details: {
            ...transaction.details,
            check_in: { ...checkInData },
          },
        }
      : transaction,
  );

  return {
    ...state,
    currentTransaction: updatedCurrentTransaction,
    transactions: updatedTransactions,
    checkIn: {
      error: null,
      status: ACTION_STATUSES.FULFILLED,
    },
  };
};

const checkInFailureHandler = (
  state: TransactionsState,
  { payload: error }: ActionType<typeof actions.checkIn.failure>,
): TransactionsState => ({
  ...state,
  checkIn: {
    error: error.error,
    status: ACTION_STATUSES.REJECTED,
  },
});

const setCurrentTransactionHandler = (
  state: TransactionsState,
  { payload }: ActionType<typeof actions.setCurrentTransaction>,
): TransactionsState => ({
  ...state,
  currentTransaction: { ...payload },
});

const resetCurrentTransactionHandler = (state: TransactionsState): TransactionsState => ({
  ...state,
  currentTransaction: null,
});

const transactionsReducer = createReducer(initialState)
  .handleAction(actions.getTransaction.request, getTransactionRequestHandler)
  .handleAction(actions.getTransaction.success, getTransactionSuccessHandler)
  .handleAction(actions.getTransaction.failure, getTransactionFailureHandler)
  .handleAction(actions.resetTransaction, resetTransactionHandler)
  .handleAction(actions.getTransactions.request, getTransactionsRequestHandler)
  .handleAction(actions.getTransactions.success, getTransactionsSuccessHandler)
  .handleAction(actions.getTransactions.failure, getTransactionsFailureHandler)
  .handleAction(actions.getPastTransactions.request, getPastTransactionsRequestHandler)
  .handleAction(actions.getPastTransactions.success, getPastTransactionsSuccessHandler)
  .handleAction(actions.getPastTransactions.failure, getPastTransactionsFailureHandler)
  .handleAction(actions.getRedirectTransaction.request, getRedirectTransactionRequestHandler)
  .handleAction(actions.getRedirectTransaction.success, getRedirectTransactionSuccessHandler)
  .handleAction(actions.getRedirectTransaction.failure, getRedirectTransactionFailureHandler)
  .handleAction(actions.resetRedirectTransaction, resetRedirectTransactionHandler)
  .handleAction(actions.patchTransaction.request, patchTransactionRequestHandler)
  .handleAction(actions.patchTransaction.success, patchTransactionSuccessHandler)
  .handleAction(actions.patchTransaction.failure, patchTransactionFailureHandler)
  .handleAction(actions.resetPatchTransactionStatus, resetPatchTransaction)
  .handleAction(actions.checkIn.request, checkInRequestHandler)
  .handleAction(actions.checkIn.success, checkInSuccessHandler)
  .handleAction(actions.checkIn.failure, checkInFailureHandler)
  .handleAction(actions.setCurrentTransaction, setCurrentTransactionHandler)
  .handleAction(actions.resetCurrentTransaction, resetCurrentTransactionHandler);

export default transactionsReducer;
